/**
 * The topbar date filter — presets, resolution and labelling.
 *
 * A filter is stored as an *intent* (`{ preset }`), not as two frozen dates, so
 * "Last 7 days" still means the last seven days when the tab is left open
 * overnight. Only `custom` carries explicit bounds. `resolve()` turns the intent
 * into the `from`/`to` the API takes — both inclusive, both `yyyy-MM-dd`.
 */

import { endOfMonth, format, startOfMonth, startOfYear, subDays, subMonths } from 'date-fns';
import { toIsoDate } from './utils';

export const DATE_PRESETS = [
  'all',
  'today',
  'yesterday',
  '7d',
  '30d',
  '90d',
  'month',
  'lastMonth',
  'year',
  'custom',
] as const;

export type DatePreset = (typeof DATE_PRESETS)[number];

export interface DateFilter {
  preset: DatePreset;
  /** Only meaningful for `custom`; ignored otherwise. */
  from?: string;
  to?: string;
}

export interface ResolvedRange {
  /** Undefined for `all` — the API then returns its unfiltered view. */
  from?: string;
  to?: string;
  /** Short form for the topbar button, e.g. "1 – 26 Jul 2026". */
  label: string;
  /** Long form for card subtitles, e.g. "01 Jul 2026 → 26 Jul 2026". */
  longLabel: string;
  /** True whenever the dashboard is showing something other than all time. */
  active: boolean;
}

export const PRESET_LABELS: Record<DatePreset, string> = {
  all: 'All time',
  today: 'Today',
  yesterday: 'Yesterday',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  month: 'This month',
  lastMonth: 'Last month',
  year: 'This year',
  custom: 'Custom range',
};

/** Presets offered in the popover, in the order they are shown. */
export const QUICK_PRESETS: DatePreset[] = [
  'all',
  'today',
  'yesterday',
  '7d',
  '30d',
  '90d',
  'month',
  'lastMonth',
  'year',
];

export const DEFAULT_DATE_FILTER: DateFilter = { preset: 'all' };

/** Turns a stored intent into concrete bounds. `now` is injectable for tests. */
export function resolveDateFilter(filter: DateFilter, now: Date = new Date()): ResolvedRange {
  const today = toIsoDate(now);

  switch (filter.preset) {
    case 'today':
      return labelled(today, today);
    case 'yesterday': {
      const day = toIsoDate(subDays(now, 1));
      return labelled(day, day);
    }
    case '7d':
      return labelled(toIsoDate(subDays(now, 6)), today);
    case '30d':
      return labelled(toIsoDate(subDays(now, 29)), today);
    case '90d':
      return labelled(toIsoDate(subDays(now, 89)), today);
    case 'month':
      return labelled(toIsoDate(startOfMonth(now)), today);
    case 'lastMonth': {
      const previous = subMonths(now, 1);
      return labelled(toIsoDate(startOfMonth(previous)), toIsoDate(endOfMonth(previous)));
    }
    case 'year':
      return labelled(toIsoDate(startOfYear(now)), today);
    case 'custom': {
      // A half-filled custom range is treated as open-ended rather than ignored,
      // so the picker stays usable while the user is still typing a date.
      const from = filter.from || filter.to;
      const to = filter.to || today;
      if (!from) return allTime();
      return labelled(from > to ? to : from, to);
    }
    case 'all':
    default:
      return allTime(now);
  }
}

function allTime(now: Date = new Date()): ResolvedRange {
  return {
    from: undefined,
    to: undefined,
    // With no filter applied the button keeps showing today's date, which is what
    // the mockup's chip did — the control looks unchanged until it is used.
    label: format(now, 'dd MMM yyyy'),
    longLabel: 'All time',
    active: false,
  };
}

function labelled(from: string, to: string): ResolvedRange {
  return {
    from,
    to,
    label: shortRangeLabel(from, to),
    longLabel: `${format(parse(from), 'dd MMM yyyy')} → ${format(parse(to), 'dd MMM yyyy')}`,
    active: true,
  };
}

/** "26 Jul 2026" / "1 – 26 Jul 2026" / "28 Jun – 26 Jul 2026" / "28 Dec 2025 – 3 Jan 2026" */
export function shortRangeLabel(from: string, to: string): string {
  const start = parse(from);
  const end = parse(to);

  if (from === to) return format(start, 'd MMM yyyy');
  if (start.getFullYear() !== end.getFullYear()) {
    return `${format(start, 'd MMM yyyy')} – ${format(end, 'd MMM yyyy')}`;
  }
  if (start.getMonth() !== end.getMonth()) {
    return `${format(start, 'd MMM')} – ${format(end, 'd MMM yyyy')}`;
  }
  return `${format(start, 'd')} – ${format(end, 'd MMM yyyy')}`;
}

/** Parses `yyyy-MM-dd` as a *local* date — `new Date('2026-07-26')` would be UTC. */
function parse(value: string): Date {
  const [year, month, day] = value.split('-').map(Number);
  return new Date(year ?? 1970, (month ?? 1) - 1, day ?? 1);
}

/** Number of days a resolved range covers, inclusive. Undefined for all time. */
export function rangeLengthInDays(range: ResolvedRange): number | undefined {
  if (!range.from || !range.to) return undefined;
  const ms = parse(range.to).getTime() - parse(range.from).getTime();
  return Math.round(ms / 86_400_000) + 1;
}
